import * as React from 'react';
import { useLocation } from 'react-router-dom';
import {
  getGetApiTenantsTenantSlugCustomerSettingsQueryKey,
  useGetApiTenantsTenantSlugCustomerSettings
} from '@cdf/api-client-react';
import type {
  CustomerSkinEffective,
  CustomerSkinPalette,
  CustomerSkinTheme,
  HomeLayout,
  NavigationLayout
} from '@cdf/api-client-react';

import { useUser } from '@/components/auth/user-provider';
import { useTheme } from '@/components/theme/theme-provider';
import { defaultEntity, selectedEntity } from '@/features/tenancy/context-navigation';
import { readableForeground, skinFontStack, skinTheme, skinTokens, type SkinThemeName } from '@/lib/customer-skin';
import type { LayoutAuthority } from '@/lib/managed-mode';

export type CustomerLogo = { dataUrl: string; altText: string } | null;

/** Configured header surface for the active theme. */
export interface CustomerHeaderSurface {
  background: string;
  foreground: string;
}

export interface CustomerSkinContextValue {
  /** Uploaded scoped logo, or null when the customer has none. */
  logo: CustomerLogo;
  /** Resolved effective skin; null until settings load. */
  skin: CustomerSkinEffective | null;
  /** Active colour theme. */
  theme: SkinThemeName;
  /** Semantic colours for the active theme. */
  themeValues: CustomerSkinTheme;
  /** Named palette entries that carry a value. */
  palette: CustomerSkinPalette;
  /** Configured header surface for the active theme; null keeps the app header. */
  header: CustomerHeaderSurface | null;
  /** Bundled font stack when the skin requests one; null keeps the app default. */
  fontFamily: string | null;
  /** Resolved platform, tenant or entity navigation layout. */
  navigationLayout: NavigationLayout | null;
  /** Resolved Home section order and visibility. */
  homeLayout: HomeLayout | null;
  /** Server-derived layout edit rights for this selected entity. */
  layoutAuthority: LayoutAuthority | null;
}

const emptyContext: CustomerSkinContextValue = {
  logo: null,
  skin: null,
  theme: 'light',
  themeValues: {},
  palette: {},
  header: null,
  fontFamily: null,
  navigationLayout: null,
  homeLayout: null,
  layoutAuthority: null
};

const CustomerSkinContext = React.createContext<CustomerSkinContextValue>(emptyContext);

/** Generic skin context for renderers and decorative components. */
export function useCustomerSkin(): CustomerSkinContextValue {
  return React.useContext(CustomerSkinContext);
}

export function useCustomerLogo(): CustomerLogo {
  return useCustomerSkin().logo;
}

/** Scoped under EntityCacheBoundary so an account/entity switch cannot reuse another customer's query. */
export function CustomerBrandProvider({ children }: { children: React.ReactNode }) {
  const user = useUser();
  const { pathname } = useLocation();
  const { resolvedTheme } = useTheme();
  const entity = selectedEntity(user?.tenants ?? [], pathname) ?? defaultEntity(user?.tenants ?? []);
  const query = useGetApiTenantsTenantSlugCustomerSettings(entity?.slug ?? '', {
    query: {
      queryKey: getGetApiTenantsTenantSlugCustomerSettingsQueryKey(entity?.slug ?? ''),
      enabled: Boolean(entity?.slug && entity.status === 'active'),
      retry: false,
      staleTime: 30_000
    }
  });
  const settings = query.data?.entityId === entity?.id ? query.data : null;

  const value = React.useMemo<CustomerSkinContextValue>(() => {
    const skin = settings?.skin.effective ?? null;
    const themeValues = skinTheme(skin, resolvedTheme);
    const headerBackground = themeValues.headerBackground ?? null;
    const headerForeground =
      themeValues.headerForeground ?? (headerBackground ? readableForeground(headerBackground) : null);
    return {
      logo: settings?.logo ? { dataUrl: settings.logo.dataUrl, altText: settings.logo.altText } : null,
      skin,
      theme: resolvedTheme,
      themeValues,
      palette: skin?.palette ?? {},
      header: headerBackground && headerForeground ? { background: headerBackground, foreground: headerForeground } : null,
      fontFamily: skinFontStack(skin?.typography),
      navigationLayout: settings?.navigationLayout.effective ?? null,
      homeLayout: settings?.homeLayout.effective ?? null,
      layoutAuthority: settings ? {
        entityId: settings.entityId,
        canManageTenant: settings.canManageTenant,
        canManageEntity: settings.canManageEntity,
        navigationEntityUnlocked: settings.navigationLayout.allowEntityOverride,
        homeEntityUnlocked: settings.homeLayout.allowEntityOverride
      } : null
    };
  }, [settings, resolvedTheme]);

  // Write the effective skin to the document root so descendants and portalled
  // surfaces share the tokens, and remove every value this provider wrote before
  // the next theme, entity or account applies.
  const appliedTokens = React.useRef<string[]>([]);
  React.useEffect(() => {
    const root = document.documentElement;
    for (const name of appliedTokens.current) {
      root.style.removeProperty(name);
    }
    const legacyPrimary = value.theme === 'dark' ? settings?.effective.darkPrimary : settings?.effective.lightPrimary;
    const legacyForeground = value.theme === 'dark' ? settings?.foreground.darkPrimary : settings?.foreground.lightPrimary;
    const tokens = skinTokens(value, { primary: legacyPrimary, foreground: legacyForeground });
    appliedTokens.current = Object.keys(tokens);
    for (const [name, token] of Object.entries(tokens)) {
      root.style.setProperty(name, token);
    }
    return () => {
      for (const name of appliedTokens.current) {
        root.style.removeProperty(name);
      }
      appliedTokens.current = [];
    };
  }, [value, settings]);

  return <CustomerSkinContext.Provider value={value}>{children}</CustomerSkinContext.Provider>;
}
